import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { Store } from '@zeroth/shared';

interface StoreSelectionState {
  // Current selected store
  currentStoreId: string | null;
  currentStore: Store | null;

  // All available stores
  stores: Store[];

  // Loading states
  isLoading: boolean;
  isSwitching: boolean;

  // View mode for multi-store dashboards
  viewMode: 'single' | 'all-stores';

  // Actions
  setStores: (stores: Store[]) => void;
  setCurrentStore: (storeId: string | null) => void;
  switchStore: (storeId: string) => Promise<void>;
  setViewMode: (mode: 'single' | 'all-stores') => void;
  setLoading: (loading: boolean) => void;
  reset: () => void;
}

const initialState = {
  currentStoreId: null,
  currentStore: null,
  stores: [],
  isLoading: false,
  isSwitching: false,
  viewMode: 'single' as const,
};

export const useStoreSelectionStore = create<StoreSelectionState>()(
  persist(
    (set, get) => ({
      ...initialState,

      setStores: (stores: Store[]) => {
        const { currentStoreId } = get();

        if (stores.length === 0) {
          set({
            stores,
            currentStoreId: null,
            currentStore: null,
          });
          return;
        }

        const currentStore = currentStoreId
          ? stores.find((s) => s.id === currentStoreId) || null
          : null;
        const nextStore = currentStore || stores[0];

        // If the selected store was removed, advance to the first remaining store.
        if (!currentStoreId || !currentStore) {
          set({
            stores,
            currentStoreId: nextStore.id,
            currentStore: nextStore,
          });
        } else {
          // Update stores and refresh current store reference
          set({
            stores,
            currentStore,
            currentStoreId: currentStoreId,
          });
        }
      },

      setCurrentStore: (storeId: string | null) => {
        const { stores } = get();
        const store = storeId ? stores.find((s) => s.id === storeId) || null : null;
        set({
          currentStoreId: storeId,
          currentStore: store,
        });
      },

      switchStore: async (storeId: string) => {
        const { stores, currentStoreId } = get();

        // Don't switch if already selected
        if (storeId === currentStoreId) return;

        const store = stores.find((s) => s.id === storeId);
        if (!store) {
          console.error(`Store with id ${storeId} not found`);
          return;
        }

        set({ isSwitching: true });

        try {
          // Allow time for any pending queries to cancel
          await new Promise((resolve) => setTimeout(resolve, 50));

          set({
            currentStoreId: storeId,
            currentStore: store,
            viewMode: 'single',
          });
        } finally {
          set({ isSwitching: false });
        }
      },

      setViewMode: (mode: 'single' | 'all-stores') => {
        set({ viewMode: mode });
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      reset: () => {
        set(initialState);
      },
    }),
    {
      name: 'zeroth-store-selection',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        currentStoreId: state.currentStoreId,
        viewMode: state.viewMode,
      }),
    }
  )
);

// Selector hooks for common access patterns
export const useCurrentStoreId = () => useStoreSelectionStore((state) => state.currentStoreId);
export const useCurrentStore = () => useStoreSelectionStore((state) => state.currentStore);
export const useStores = () => useStoreSelectionStore((state) => state.stores);
export const useIsStoreSwitching = () => useStoreSelectionStore((state) => state.isSwitching);
